import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  ShieldAlert, Loader2, CheckCircle2, AlertTriangle, XCircle, Copy,
} from 'lucide-react';

const DmarcCheckCard = ({ dmarcDomain, setDmarcDomain, dmarcChecking, dmarcResult, runDmarcCheck }) => (
  <Card className="border-[#E5E7EB]" data-testid="dmarc-check-card">
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-[#002FA7]" />DMARC Record Check
        </CardTitle>
        <div className="flex items-center gap-2">
          <Input
            value={dmarcDomain}
            onChange={e => setDmarcDomain(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') runDmarcCheck(); }}
            placeholder="kstreak.com (default: sender domain)"
            className="h-8 text-xs w-64"
            data-testid="dmarc-domain-input"
          />
          <Button
            size="sm" onClick={runDmarcCheck} disabled={dmarcChecking}
            className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="run-dmarc-check-btn"
          >
            {dmarcChecking ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldAlert className="w-3 h-3 mr-1" />}
            Run Check
          </Button>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <p className="text-[11px] text-[#4B5563] mb-3">
        Queries <code className="text-[10px] bg-[#F3F4F6] px-1 rounded">_dmarc.&lt;domain&gt;</code> and classifies the policy.
        Catches spoofing exposure before your first phishing incident — <strong>missing or <code>p=none</code> means attackers can send as you</strong>.
      </p>
      {dmarcResult ? (
        <div data-testid="dmarc-results" className="space-y-3">
          {(() => {
            const { severity, status, summary, domain, lookup_host, records, parsed, warnings } = dmarcResult;
            const bg = severity === 'ok' ? 'bg-[#34C759]/10' : severity === 'medium' ? 'bg-[#FF9500]/10' : 'bg-[#FF3B30]/10';
            const fg = severity === 'ok' ? '#34C759' : severity === 'medium' ? '#FF9500' : '#FF3B30';
            const Icon = severity === 'ok' ? CheckCircle2 : severity === 'medium' ? AlertTriangle : XCircle;
            return (
              <>
                <div className={`p-3 rounded-lg ${bg}`}>
                  <p className="text-sm font-semibold flex items-center gap-2" style={{ color: fg }}>
                    <Icon className="w-4 h-4" />
                    {summary}
                  </p>
                  <p className="text-[10px] text-[#4B5563] mt-1">
                    <code>{lookup_host}</code> · status: <code>{status}</code>
                  </p>
                </div>

                {records.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">DNS Record{records.length > 1 ? 's' : ''}</p>
                    <div className="space-y-1">
                      {records.map((r, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <code className="flex-1 text-[11px] bg-[#F3F4F6] px-2 py-1.5 rounded font-mono break-all">{r}</code>
                          <button
                            onClick={() => { navigator.clipboard.writeText(r); toast.success('Record copied'); }}
                            className="text-[#9CA3AF] hover:text-[#002FA7]" title="Copy record"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {parsed && Object.keys(parsed).length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">Parsed Tags</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                      {Object.entries(parsed).filter(([, v]) => v != null).map(([k, v]) => (
                        <div key={k} className="bg-[#F9FAFB] border border-[#E5E7EB] rounded px-2 py-1">
                          <span className="text-[#9CA3AF] block text-[9px] uppercase">{k}</span>
                          <code className="text-[11px] text-[#0A0A0A] break-all">{String(v)}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {warnings.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">Recommendations ({warnings.length})</p>
                    <ul className="space-y-1.5">
                      {warnings.map((w, i) => (
                        <li key={i} className="flex items-start gap-2 text-[11px] text-[#4B5563] bg-[#FFF9E6] border border-[#FFE58F] rounded p-2">
                          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#FF9500]" />
                          <span dangerouslySetInnerHTML={{
                            __html: w.replace(/`([^`]+)`/g, '<code class="text-[10px] bg-white px-1 rounded border border-[#E5E7EB]">$1</code>')
                                    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>'),
                          }} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-[10px] text-[#9CA3AF]">Domain: <strong>{domain}</strong></p>
              </>
            );
          })()}
        </div>
      ) : (
        <p className="text-[11px] text-[#9CA3AF] italic">Click <strong>Run Check</strong> to query the DMARC record for your sender domain.</p>
      )}
    </CardContent>
  </Card>
);

export default DmarcCheckCard;
